import { Button, CompareIcon, Popover } from '@iheartjane/reefer';
import type { ButtonProps, MarginProperties } from '@iheartjane/reefer';
import { useEffect } from 'react';

import { CompareHint } from './compareHint';
import { useCompare } from './compareProvider';
import { useCompareHint } from './useCompareHint';

/** How long the first-run popover stays open before auto-dismissing. */
const HINT_AUTO_DISMISS_MS = 3000;

/** Closes the popover a few seconds after it opens so the hint never lingers. */
const AutoDismiss = ({ onDismiss }: { onDismiss: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, HINT_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [onDismiss]);
  return null;
};

export const CompareButton = (marginProps: MarginProperties) => {
  const { enabled, isActive, toggleActive } = useCompare();
  const { hintWasSeen, markHintSeen } = useCompareHint();

  if (!enabled) return null;

  const buttonProps = {
    ...marginProps,
    variant: isActive ? 'primary' : ('tertiary' as ButtonProps['variant']),
    startIcon: <CompareIcon size="sm" />,
    label: isActive ? 'Done' : 'Compare',
    'aria-label': isActive ? 'Exit compare mode' : 'Enter compare mode',
    'data-testid': 'compare-button',
  };

  if (hintWasSeen) return <Button {...buttonProps} onClick={toggleActive} />;

  // First-run hint: clicking Compare both toggles compare mode and opens the
  // popover. We persist "seen" on close (not open) so the read-once external
  // store doesn't flip mid-display and unmount the popover.
  return (
    <Popover
      target={<Button {...buttonProps} />}
      label="How to compare products"
      variant="info"
      onClose={markHintSeen}
    >
      {({ closePopover }) => (
        <Popover.Content>
          <CompareHint />
          <AutoDismiss onDismiss={closePopover} />
        </Popover.Content>
      )}
    </Popover>
  );
};
